<script>
    import { createEventDispatcher } from 'svelte';
    import Spinner from 'svelte-spinner';
    import { Tooltip } from "sveltestrap";
    import { tooltip } from "@svelte-plugins/tooltips";

    import { formatDateTime } from '../../date-time.js';
    import { vptServer, token, getTooltipDisplayOptions } from '../../stores.js';
    import { referenceDataEarlyRiskTabList } from "../../reference-data.js";

    import AimTooltip from '../../component/AimTooltip.svelte';

    const dispatch = createEventDispatcher();

    export let id;
    export let todaysConfig;
    export let selectedInmateAspect;

    let earlyRiskRecord;
    let isInEarlyRiskPeriod;
    let targetedTabCount;

    $: {
        if (todaysConfig && todaysConfig.earlyRiskChecklistUrn) {
            earlyRiskRecord = null;
            getEarlyRiskRecord();
        }
    }

    async function getEarlyRiskRecord() {
        await fetch(`${$vptServer}/inmate/${id}/earlyrisk/overview`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            isInEarlyRiskPeriod = data.indicatorEarlyRiskPeriod;
            dispatch('dataLoad', { isAspectArchived: !isInEarlyRiskPeriod });
            earlyRiskRecord = data;

            // If the inmate has an early risk record, update the tabs to include the targeted status
            if (earlyRiskRecord && earlyRiskRecord.assessmentTabs) {
                targetedTabCount = countTargetedTabs();

                earlyRiskRecord.assessmentTabs = referenceDataEarlyRiskTabList
                .filter(tab => tab.checklistUrn == earlyRiskRecord.checklistUrn)
                .map(tab => {
                    return { id : tab.vptEarlyRiskTabUrn, name : tab.name,  order : tab.ordering, 
                        targeted : earlyRiskRecord.assessmentTabs.find((t) => t.vptEarlyRiskTabUrn == tab.vptEarlyRiskTabUrn).targetedOnTab,
                        targetingTrigger : tab.targetingTrigger}
                });
            }
        })
        .catch(error => {
            console.error("Error getting inmate early risk overview");
            console.error(error);
            return;
        });
    }

    function countTargetedTabs() {
        return earlyRiskRecord.assessmentTabs.filter(t => {return t.targetedOnTab}).length;
    }
</script>

<style>
    .inmate-widget {
        width: 140px;
    }
    
    .inmate-widget .no-records-container {
        display: flex;
        flex-direction: column;
    }

    .inmate-widget .no-records-container img {
        height: 38px;
        padding: 5px 0;
    }

    .inmate-widget .no-records {
        padding: 0 10px 0 10px;
    }

    .list-widget-body {
        padding-bottom: 3px;
        justify-content: space-around;
    }

    .early-risk-category {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .early-risk-category img {
        height: 15px;
    }

    .early-risk-category span {
        font-size: 11px;
        font-weight: 700;
        margin-left: 5px;
    }

    .early-risk-category span.hidden {
        filter: opacity(50%);
        font-weight: 500;
    }

    .no-sections-message {
        width: 100%;
        text-align: center;
        display: block;
        padding-top: 5px;
    }
</style>

{#if todaysConfig && todaysConfig.earlyRiskChecklistUrn && isInEarlyRiskPeriod}
    <div class="inmate-widget">
        {#if earlyRiskRecord}
            <button class="button-reset focus-outline card" on:click class:selected={selectedInmateAspect === "Early Risk"}>
                <div class="card-body">
                    <div class="widget-container">
                        
                        <span id="early-risk-widget-header" class="widget-label" class:widget-label-targeted={earlyRiskRecord.indicatorTargeted}>
                            {#if earlyRiskRecord.indicatorTargeted}
                                <img id="targeted-indicator-early-risk" src="/assets/target-indicator.svg" alt="Targeted on the Early Risk aspect" />
                                <Tooltip target="targeted-indicator-early-risk" placement="bottom">Targeted on the Early Risk aspect</Tooltip>
                            {/if}
                            Early Risk
                            <Tooltip target="early-risk-widget-header" placement="bottom">{todaysConfig.earlyRiskFlagDescription}</Tooltip>
                        </span>

                        <div class="widget-content">
                            {#if earlyRiskRecord.indicatorCompleted == false && (earlyRiskRecord.assessmentUrn == null || earlyRiskRecord.assessmentUrn == 0)}
                                <div class="no-records-container">
                                    <img src="/assets/exclamation-danger.svg" alt="{todaysConfig.earlyRiskAssessmentLabelName} has not been started" />        
                                    <span class="no-records">The {todaysConfig.earlyRiskAssessmentLabelName} has not been started.</span>    
                                </div>
                            {:else}
                                <div class="list-widget">
                                    <div class="list-widget-body">
                                        {#if targetedTabCount == 0 && earlyRiskRecord.indicatorCompleted == true}
                                            <span class="no-sections-message">No sections were identified on the RSRA.</span>
                                        {:else}
                                            {#each earlyRiskRecord.assessmentTabs as tab}
                                                {#if tab.targetingTrigger}
                                                    <div id="er-widget-{tab.id}" class="early-risk-category">
                                                        {#if tab.targeted}
                                                            <img src="/assets/exclamation-triangle-danger.svg" alt="{todaysConfig.earlyRiskFlagDescription} generated in this section" />
                                                            <Tooltip target="er-widget-{tab.id}" placement="bottom">
                                                                {todaysConfig.earlyRiskAssessmentLabelName} indicated {tab.name} as a possible risk factor
                                                            </Tooltip>
                                                            <span>{tab.name}</span>
                                                        {:else}
                                                            {#if earlyRiskRecord.indicatorCompleted == false}
                                                                <span class="hidden">RSRA Incomplete</span>
                                                            {:else}
                                                                <span class="hidden">{tab.name}</span>
                                                            {/if}
                                                        {/if}
                                                    </div>
                                                {/if}
                                            {/each}
                                        {/if}
                                    </div>

                                    <span class="list-widget-footer" use:tooltip={{
                                        content: {
                                            component: AimTooltip,
                                            props: {tooltipLines: ["Arrival Date: " + formatDateTime(earlyRiskRecord.dateArrived)]}
                                        },
                                        ...getTooltipDisplayOptions()
                                        }}>
                                        {#if earlyRiskRecord.hoursSinceArrival == 0}
                                            Just Arrived
                                        {:else}
                                            Arrived {earlyRiskRecord.hoursSinceArrival} hour{earlyRiskRecord.hoursSinceArrival == 1 ? '' : 's'} ago
                                        {/if}
                                    </span>
                                </div>
                            {/if}
                        </div>
                    </div>
                </div>
            </button>
        {:else}
            <div class="card" class:selected={selectedInmateAspect === "Early Risk"}>
                <div class="card-body">
                    <div class="widget-container">  
                        <div class="widget-spinner">
                            <Spinner size="100" speed="750" color="#3d5980" thickness="3" gap="15" />
                        </div>
                    </div>
                </div>
            </div>
        {/if}
    </div>
{/if}